import { TIPO_INSPECAO } from "@/contexts/cadastro/estabelecimento";
import { useTermoNotificacao } from "@/contexts/fiscalizacao/termoNotificacao/hooks/useTermoNotificacao";
import { PageLoader } from "@/shared/components/PageLoader/PageLoader";
import { cpfMask } from "@/shared/utils/cpfMask";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Grid,
  Input,
  Radio,
  Result,
  Row,
  Space,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router";

export function VisualizacaoTermoNotificacao() {
  const screens = Grid.useBreakpoint();
  const { id } = useParams<{ id: string }>();
  const { getTermoNotificacaoByIdApi } = useTermoNotificacao();
  const navigate = useNavigate();

  const {
    data: termoNotificacao,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["TERMO_NOTIFICACAO", id],
    enabled: !!id,
    queryFn: () => getTermoNotificacaoByIdApi(Number(id)),
  });

  const handleBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    document.title = "Inspeção Mobile - Visualização Termo de Notificação";
  }, []);

  if (isLoading) {
    return <PageLoader />;
  }

  if (error) {
    return (
      <Result
        status="500"
        title="Parece que aconteceu algum problema com o servidor."
        subTitle="Tente novamente mais tarde ou entre em contato com o suporte."
        extra={
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
              Voltar
            </Button>
            <Button onClick={() => refetch()}>Tentar Novamente</Button>
          </Space>
        }
      />
    );
  }

  return (
    <>
      <Space direction="vertical" style={{ width: "100%" }} align="center">
        <Typography.Title
          level={screens.xs ? 4 : 3}
          style={{ textAlign: "center" }}
        >
          Termo notificação {termoNotificacao?.numero}
        </Typography.Title>
      </Space>
      <Divider />

      <Form layout="vertical">
        <Row gutter={[8, 8]}>
          <Col xs={24} sm={12}>
            <Form.Item label="Estabelecimento">
              <Input
                disabled
                value={`${termoNotificacao?.estabelecimento.numeroInspecao} - ${termoNotificacao?.estabelecimento ? TIPO_INSPECAO[termoNotificacao.estabelecimento.tipoInspecao] : "SISE"} - ${termoNotificacao?.estabelecimento.nome}`}
              />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item label="Teor da Notificação">
              <Input.TextArea
                rows={4}
                disabled
                value={termoNotificacao?.teor}
              />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item label="Motivo" rules={[{ required: true }]}>
              <Input
                disabled
                value={termoNotificacao?.motivos
                  .map((motivo) => motivo.nome?.toUpperCase())
                  .join(", ")}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item colon={false} label="Exige Plano de Ação?">
              <Radio.Group disabled value={termoNotificacao?.exigeSaneamento}>
                <Radio value={true}>Sim</Radio>
                <Radio value={false}>Não</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>

          {termoNotificacao?.exigeSaneamento && (
            <Col xs={24}>
              <Form.Item label="Prazo para atendimento">
                <DatePicker
                  format={"DD/MM/YYYY"}
                  disabled
                  value={
                    termoNotificacao?.prazoAtendimento
                      ? dayjs(termoNotificacao.prazoAtendimento)
                      : undefined
                  }
                />
              </Form.Item>
            </Col>
          )}
        </Row>

        <Divider orientation="left">
          <Typography.Title level={screens.xs ? 5 : 4}>
            Assinaturas
          </Typography.Title>
        </Divider>

        <Col xs={24} sm={24}>
          <Form.Item>
            <Radio.Group
              disabled
              value={termoNotificacao?.statusAssinaturaFiscalizado}
            >
              <Radio value="ASSINADO_FISICAMENTE">Assinatura Fisica</Radio>

              <Radio value="ASSINADO_DIGITALMENTE">Assinatura Digital</Radio>

              <Radio value="RECUSADO">Recusado</Radio>

              <Radio value="AUSENTE">Ausente</Radio>

              <Radio value="IMPOSSIBILITADO">Impossibilitado</Radio>

              <Radio value="NAO_ALFABETIZADO">Não Alfabetizado</Radio>
            </Radio.Group>
          </Form.Item>
        </Col>

        {termoNotificacao?.statusAssinaturaFiscalizado &&
          termoNotificacao?.statusAssinaturaFiscalizado !==
            "ASSINADO_FISICAMENTE" && (
            <Row
              style={{ marginTop: 20 }}
              gutter={[24, 24]}
              justify={"space-around"}
            >
              <Col xs={24} sm={24} md={12} lg={12} xl={8}>
                <Row justify={"center"} style={{ marginTop: 10 }}>
                  <Col
                    span={24}
                    style={{
                      border: "1px solid #ccc",
                      height: 400,

                      borderRadius: 8,
                    }}
                  >
                    <Form.Item>
                      {termoNotificacao?.assinaturaServidor && (
                        <img
                          src={termoNotificacao.assinaturaServidor}
                          alt="Assinatura Servidor"
                          style={{ width: "90%", height: "90%" }}
                        />
                      )}
                    </Form.Item>

                    <Form.Item name="assinaturaServidor" hidden>
                      <Input type="hidden" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row justify={"center"} style={{ marginTop: 10 }}>
                  <Col span={24} style={{ textAlign: "center" }}>
                    <Typography.Text strong>
                      Assinatura Notificante
                    </Typography.Text>
                  </Col>
                </Row>

                <Row
                  justify={"center"}
                  style={{ marginTop: 10 }}
                  gutter={[8, 8]}
                >
                  <Col xs={24} sm={8}>
                    <Form.Item label="Nome">
                      <Input disabled value={termoNotificacao.usuario?.nome} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={8}>
                    <Form.Item label="CPF">
                      <Input
                        disabled
                        value={cpfMask(termoNotificacao.usuario?.cpfCnpj || "")}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={8}>
                    <Form.Item label="Função">
                      <Input disabled value={"NOTIFICANTE"} />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>

              {termoNotificacao?.statusAssinaturaFiscalizado ===
                "ASSINADO_DIGITALMENTE" && (
                <Col xs={24} sm={24} md={12} lg={12} xl={8}>
                  <Row justify={"center"} style={{ marginTop: 10 }}>
                    <Col
                      span={24}
                      style={{
                        border: "1px solid #ccc",
                        height: 400,
                        borderRadius: 8,
                      }}
                    >
                      <Form.Item>
                        {termoNotificacao.assinaturaFiscalizado && (
                          <img
                            src={termoNotificacao.assinaturaFiscalizado}
                            alt="Assinatura Fiscalizado"
                            style={{ width: "100%", height: "100%" }}
                          />
                        )}
                      </Form.Item>

                      <Form.Item name="assinaturaFiscalizado" hidden>
                        <Input type="hidden" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row
                    justify={"center"}
                    style={{ marginTop: 10 }}
                    gutter={[8, 8]}
                  >
                    <Col span={24} style={{ textAlign: "center" }}>
                      <Typography.Text strong>
                        Assinatura Notificado
                      </Typography.Text>
                    </Col>

                    <Col xs={24} sm={8}>
                      <Form.Item label="Nome">
                        <Input
                          disabled
                          value={termoNotificacao.nomeAssinanteFiscalizado}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={8}>
                      <Form.Item label="CPF">
                        <Input
                          disabled
                          value={cpfMask(
                            termoNotificacao.cpfAssinanteFiscalizado || ""
                          )}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={8}>
                      <Form.Item label="Função">
                        <Input
                          disabled
                          value={termoNotificacao.funcaoAssinanteFiscalizado}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              )}
            </Row>
          )}

        <Divider />

        <Row justify={"center"} gutter={[10, 10]}>
          <Col>
            <Button
              htmlType="button"
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
              style={{ width: 200 }}
            >
              Voltar
            </Button>
          </Col>
        </Row>
      </Form>
    </>
  );
}
